import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ccf-consumer-card-details-tab.module.scss';
import {
  CcfStrokeIcon,
  CcfTypography,
  CcfBox,
  CcfButton,
  CcfUpArrowIcon,
  CcfPrimaryAddressIcon,
  CcfSecondaryAddressIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import {
  activeContactSelector,
  updateActiveContact,
} from '../../../ccf-inbox/ccf-inbox.slice';
import { CcfToast } from '@nice-ccf/ui-components';

interface CcfConsumerCardDetailsTabCustomAddressRowProps {
  headerCaption: string;
  detailsMinValue: boolean;
}

/**
 * Renders a custom address row for the consumer card details tab.
 *
 *
 *
 * ```tsx
 * <CcfConsumerCardDetailsTabCustomAddressRow headerCaption="Custom Address Row" />
 * ```
 *
 * @example  props - The component props.
 * @returns  The rendered component.
 */
const CcfConsumerCardDetailsTabCustomAddressRow: React.FC<
  CcfConsumerCardDetailsTabCustomAddressRowProps
> = ({ headerCaption, detailsMinValue }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showSecondaryAddress, setShowSecondaryAddress] = useState(false);
  const activeContact = useSelector(activeContactSelector);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const dispatch = useDispatch();
  const initialFormDataForPrimaryAddress = {
    address1: activeContact?.customer?.customerPrimaryAddress?.address1 || '',
    address2: activeContact?.customer?.customerPrimaryAddress?.address2 || '',
    city: activeContact?.customer?.customerPrimaryAddress?.city || '',
    state: activeContact?.customer?.customerPrimaryAddress?.state || '',
    zip: activeContact?.customer?.customerPrimaryAddress?.zip || '',
    country: activeContact?.customer?.customerPrimaryAddress?.country || '',
  };
  const [formDataForPA, setFormDataForPA] = useState(
    initialFormDataForPrimaryAddress
  );
  const [initialformDataForPA, setInitialformDataForPA] = useState(formDataForPA);
  const initialFormDataForSecondaryAddress = {
    address1: activeContact?.customer?.customerSecondaryAddress?.address1 || '',
    address2: activeContact?.customer?.customerSecondaryAddress?.address2 || '',
    city: activeContact?.customer?.customerSecondaryAddress?.city || '',
    state: activeContact?.customer?.customerSecondaryAddress?.state || '',
    zip: activeContact?.customer?.customerSecondaryAddress?.zip || '',
    country: activeContact?.customer?.customerSecondaryAddress?.country || '',
  };
  const [formDataForSA, setFormDataForSA] = useState(
    initialFormDataForSecondaryAddress
  );  
  const [initialformDataForSA, setInitialformDataForSA] = useState(formDataForSA);
  const [translate] = useTranslator();

  useEffect(() => {
    setInitialformDataForPA(formDataForPA);
    setInitialformDataForSA(formDataForSA);
  }, [activeContact]);

  /**
   * Handles the click event when the edit button is clicked.
   * Sets the `isEditing` state to `true`.
   * @example
   */
  const handleEditClick = () => {
    if (isEditing) {
      setFormDataForPA(initialformDataForPA);
      setFormDataForSA(initialformDataForSA);
    }
    setIsEditing(!isEditing);
  } 

  /**
   * Handles the click event for the save button.
   * Sets the `isEditing` state to `false`.
   * @example
   */
  const handleSaveClick = () => {
    setIsEditing(false);
    const existingCustomerProperties = Object.assign(
      {},
      activeContact?.customer
    );
    existingCustomerProperties.customerPrimaryAddress = formDataForPA;
    if (showSecondaryAddress) {
      existingCustomerProperties.customerSecondaryAddress = formDataForSA;
    } else {
      existingCustomerProperties.customerSecondaryAddress = formDataForPA;
    }
    dispatch(
      updateActiveContact({
        id: activeContact?.id || '',
        updates: { customer: existingCustomerProperties },
      })
    );
    setShowToast(true);
    setToastMessage(translate('Addressdetailssavedsuccessfully'));
    setTimeout(() => {
      setShowToast(false);
      setToastMessage('');
    }, 3000);
  };

  /**
   * Handles the click event for expanding or collapsing the custom address row.
   * @example
   */
  const handleExpandCollapseClick = () => setIsExpanded(!isExpanded);

  /**
   * Handles the change event of an input element.
   * Updates the form data with the new value.
   * @example
   * @param e - The change event object.
   */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    source: string
  ) => {
    const { name, value } = e.target;
    if (source === 'primaryAddress') {
      setFormDataForPA({ ...formDataForPA, [name]: value });
    } else {
      setFormDataForSA({ ...formDataForSA, [name]: value });
    }
  };

  /**
   * Renders an address field with a label, name, and initial value.
   * @param label - The label for the address field.
   * @param name - The name of the address field.
   * @param value - The initial value of the address field.
   * @example
   * @returns The rendered address field component.
   */
  const renderAddressField = (
    label: string,
    name: string,
    value: string,
    source: string
  ) => (
    <div className={styles.field} style={{ marginRight: '10Px' }}>
      <CcfTypography className={styles.editModeCaptionLabel}>
        {translate(label)}
      </CcfTypography>
      <input
        className={styles.inputControl}
        id={source + name}
        type="text"
        name={name}
        value={value}
        onChange={(e: any) => handleChange(e, source)}
        style={{ border: '1px solid #D7D9DD' }}
      />
    </div>
  );

  /**
   * Renders the address display with the given label and address object.
   * @param label - The label for the address.
   * @param address - The address object containing address details.
   * @example
   * @returns The rendered address display.
   */
  const renderAddressDisplay = (label: string, address: any) => (
    <CcfBox display={'flex'} className={styles.field} id={label}>
      {label === 'PRIMARYRESIDENCE' ? (
        <CcfPrimaryAddressIcon />
      ) : (
        <CcfSecondaryAddressIcon />
      )}
      <CcfBox display={'flex'} flexDirection={'column'}>
        <CcfTypography className={styles.captionlabel}>{translate(label)}</CcfTypography>

        <CcfTypography
          style={{ height: 'auto' }}
          className={styles.captionValue}
        >
          {address?.address1} {address?.address2} <br />
          {address?.city} {address?.state}, {address?.zip} {address?.country}
        </CcfTypography>
      </CcfBox>
    </CcfBox>
  );

  return (
    <div className={styles.consumerCardDetails} id="detailsTab_address_panel">
      <div className={styles.header} style={{ height: '40px' }}>
        <div style={{ display: 'flex' }}>
          {isEditing && <CcfStrokeIcon />}
          <CcfTypography className={styles.captionHeader}>
            {translate(headerCaption)}
          </CcfTypography>
        </div>
        <CcfBox
          display="flex"
          style={{ width: isEditing ? '150px' : '100px' }}
          justifyContent="space-around"
        >
          {isEditing ? (
            <CcfBox
              style={{
                display: 'flex',
                width: '125px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <CcfButton
                id="addressTab_BtnCancel"
                className={styles.btnStyles}
                onClick={handleEditClick}
              >
                {translate('cancel')}
              </CcfButton>
              <CcfButton
                id="addressTab_BtnSave"
                className={styles.btnStyles}
                primary
                onClick={handleSaveClick}
              >
                {translate('save')}
              </CcfButton>
            </CcfBox>
          ) : (
            <>
              {isExpanded && (
                <CcfStrokeIcon
                  id="addressTab_BtnEdit"
                  className={styles.alginIcon}
                  onClick={handleEditClick}
                />
              )}
              <CcfUpArrowIcon
                id="addressTab_BtnExpand"
                style={{ transform: 'rotate(180deg)' }}
                onClick={handleExpandCollapseClick}
              />
            </>
          )}
        </CcfBox>
      </div>
      {isExpanded && (
        <div
          className={styles.body}
          style={{ backgroundColor: isEditing ? '#ECF3F8' : '#FAFAFA' }}
        >
          <div
            style={{
              width: isEditing
                ? detailsMinValue
                  ? '90%'
                  : '75%'
                : detailsMinValue
                ? '100%'
                : '90%',
            }}
            className={styles.bodyContainer}
          >
            <CcfBox
              className={
                detailsMinValue ? styles.minViewColumn : styles.disPlayFlex
              }
            >
              <div className={styles.column}>
                {isEditing ? (
                  <>
                    {renderAddressField(
                      'ADDRESS1',
                      'address1',
                      formDataForPA.address1,
                      'primaryAddress'
                    )}
                    {renderAddressField(
                      'CITY',
                      'city',
                      formDataForPA.city,
                      'primaryAddress'
                    )}
                    {renderAddressField(
                      'ZIPCODE',
                      'zip',
                      formDataForPA.zip,
                      'primaryAddress'
                    )}
                  </>
                ) : (
                  renderAddressDisplay(
                    'PRIMARYRESIDENCE',
                    activeContact?.customer?.customerPrimaryAddress
                  )
                )}
              </div>
              <div
                className={
                  detailsMinValue ? styles.minViewColumn : styles.column
                }
              >
                {isEditing ? (
                  <>
                    {renderAddressField(
                      'ADDRESS2',
                      'address2',
                      formDataForPA.address2,
                      'primaryAddress'
                    )}
                    {renderAddressField(
                      'STATE',
                      'state',
                      formDataForPA.state,
                      'primaryAddress'
                    )}
                    {renderAddressField(
                      'COUNTRY',
                      'country',
                      formDataForPA.country,
                      'primaryAddress'
                    )}
                  </>
                ) : (
                  renderAddressDisplay(
                    'BILLINGADDRESS',
                    activeContact?.customer?.customerSecondaryAddress
                  )
                )}
              </div>
            </CcfBox>
            {isEditing && (
              <>
                <div id="addAddress" className={styles.column}>
                  {translate('AddAddress')}
                </div>
                <div className={styles.column}>
                  <FormGroup>
                    <FormControlLabel
                      className={styles.checkBoxLabel}
                      control={
                        <Checkbox
                          id="showSecondaryAddress"
                          checked={!showSecondaryAddress}
                          onChange={() =>
                            setShowSecondaryAddress(!showSecondaryAddress)
                          }
                        />
                      }
                      label={translate('BillingAddresssameasPrimaryAddress')}
                    />
                  </FormGroup>
                </div>
              </>
            )}
            {showSecondaryAddress && (
              <CcfBox display="flex">
                {isEditing && (
                  <>
                    <div className={styles.column}>
                      {renderAddressField(
                        'ADDRESS1',
                        'address1',
                        formDataForSA.address1,
                        'secondaryAddress'
                      )}

                      {renderAddressField(
                        'CITY',
                        'city',
                        formDataForSA.city,
                        'secondaryAddress'
                      )}

                      {renderAddressField(
                        'ZIPCODE',
                        'zip',
                        formDataForSA.zip,
                        'secondaryAddress'
                      )}
                    </div>
                    <div className={styles.column}>
                      {renderAddressField(
                        'ADDRESS2',
                        'address2',
                        formDataForSA.address2,
                        'secondaryAddress'
                      )}
                      {renderAddressField(
                        'STATE',
                        'state',
                        formDataForSA.state,
                        'secondaryAddress'
                      )}
                      {renderAddressField(
                        'COUNTRY',
                        'country',
                        formDataForSA.country,
                        'secondaryAddress'
                      )}
                    </div>
                  </>
                )}
              </CcfBox>
            )}
          </div>
        </div>
      )}
      <CcfBox>
        <CcfToast open={showToast} type={'success'} message={toastMessage} />
      </CcfBox>
    </div>
  );
};

export default CcfConsumerCardDetailsTabCustomAddressRow;
